import { useState } from 'react';
import { CountUp } from '@/components/ui/CountUp';
import { useEntrance } from '@/shared/hooks/useCountUp';

export interface DonutSeg { key: string; label: string; value: number; color: string }

const R = 64;
const C = 2 * Math.PI * R;

/**
 * דונאט עם מקרא. מעבר עכבר על פלח או על שורה מדגיש אותו ומציג אותו במרכז.
 * בכניסה הטבעת מסתובבת פנימה (gcSpin) והפלחים גדלים לאורכם (animT בעיצוב);
 * עדכון חי לא מפעיל את זה מחדש.
 */
export function Donut({ segs, centerLabel }: { segs: DonutSeg[]; centerLabel: string }) {
  const [hot, setHot] = useState<string | null>(null);
  const e = useEntrance('donut');
  const total = segs.reduce((s, g) => s + g.value, 0);
  const hotSeg = segs.find((g) => g.key === hot);
  const share = (v: number) => (total > 0 ? `${Math.round((v / total) * 100)}%` : '—');

  let acc = 0;
  const arcs = segs.filter((g) => g.value > 0).map((g) => {
    const len = (g.value / total) * C * e;
    const arc = { ...g, dash: `${Math.max(0, len - 2)} ${C}`, off: -acc };
    acc += len;
    return arc;
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 26, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: 168, height: 168, flex: 'none' }}>
        <svg width="168" height="168" viewBox="0 0 168 168" style={{ transform: 'rotate(-90deg)', animation: 'gcSpin 1.1s var(--ease-out) both' }} aria-hidden="true">
          <circle cx="84" cy="84" r={R} fill="none" stroke="var(--surface-3)" strokeWidth="18" />
          {arcs.map((g) => (
            <circle
              key={g.key}
              onMouseEnter={() => setHot(g.key)}
              onMouseLeave={() => setHot(null)}
              cx="84" cy="84" r={R} fill="none"
              stroke={g.color}
              strokeWidth={hot === g.key ? 24 : 18}
              strokeDasharray={g.dash}
              strokeDashoffset={g.off}
              opacity={hot && hot !== g.key ? 0.35 : 1}
              style={{ cursor: 'pointer', transition: 'stroke-width .25s cubic-bezier(.2,.8,.2,1), opacity .25s ease' }}
            />
          ))}
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', textAlign: 'center' }}>
          <span style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-1px', lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>
            {hotSeg ? share(hotSeg.value) : <CountUp value={total} />}
          </span>
          <span style={{ fontSize: 12, color: 'var(--muted)', maxWidth: 100 }}>{hotSeg ? hotSeg.label : centerLabel}</span>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 170, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {segs.map((g) => (
          <div
            key={g.key}
            onMouseEnter={() => setHot(g.key)}
            onMouseLeave={() => setHot(null)}
            style={{ display: 'grid', gridTemplateColumns: '10px minmax(0,1fr) auto auto', gap: 10, alignItems: 'center', padding: '7px 10px', margin: '0 -10px', borderRadius: 8, background: hot === g.key ? 'var(--glass-soft)' : 'transparent', transition: 'background .2s ease' }}
          >
            <span style={{ width: 10, height: 10, borderRadius: 3, background: g.color }} />
            <span style={{ fontSize: 13.5 }}>{g.label}</span>
            <span style={{ fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{share(g.value)}</span>
            <span style={{ fontSize: 12, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums', minWidth: 40, textAlign: 'end' }}><CountUp value={g.value} /></span>
          </div>
        ))}
      </div>
    </div>
  );
}
